/**
 * inGameNavigation — tell the next page that the game itself sent it there.
 *
 * D44 (2026-10-09): on helix2000.com the game has one address, `/play`, and a
 * typed mode path or `?map=` is ignored (oneGameUrl.ts). But the game still
 * moves between pages with those URLs — the roster boots a hero with
 * `/?map=<save>&keepCharacter=1`, a portal with `/?map=…&transfer=…`. Those
 * navigations go through {@link navigateInGame}, which leaves a one-shot,
 * short-lived marker naming the exact target in sessionStorage (same tab
 * only). The next boot honours its URL only when the marker matches it, then
 * records the trust in `history.state` so a reload, or Back to that entry,
 * stays trusted while a URL typed into the address bar (a new entry, no
 * state, no marker) does not.
 *
 * Leaf module (no imports): the first boot import uses it.
 */

/** sessionStorage key carrying the pending in-game navigation. */
export const IN_GAME_NAV_KEY = 'h2k.inGameNav';
/** history.state key marking an entry the game booted on purpose. */
export const TRUSTED_BOOT_STATE_KEY = 'h2kTrustedBoot';
/** A marker older than this is stale (a navigation that never happened). */
export const IN_GAME_NAV_TTL_MS = 60_000;
/** Wall clock vs the navigation's high-resolution origin may disagree by a little. */
export const IN_GAME_NAV_CLOCK_SLACK_MS = 1_000;

/**
 * When this page's navigation started (epoch ms) — the age a marker is
 * measured to. Plan 26 F2: measuring to boot-time parse counted the HTML and
 * the main bundle download, so a portal on a slow load after a deploy (cold
 * cache, new hashed bundle) outlived the TTL and was treated as a typed URL.
 * `performance.timeOrigin` is the navigation start of the new document.
 */
export function navigationStartMs(): number {
  try {
    const origin = (globalThis as { performance?: { timeOrigin?: number } }).performance?.timeOrigin;
    if (typeof origin === 'number' && Number.isFinite(origin) && origin > 0) return origin;
  } catch { /* no performance API */ }
  return Date.now();
}

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function sessionStore(): StorageLike | null {
  try {
    return (globalThis as { sessionStorage?: StorageLike }).sessionStorage ?? null;
  } catch {
    return null; // storage blocked
  }
}

/** `pathname + search` of a same-origin URL (absolute or relative). */
export function pathAndSearchOf(url: string, origin = 'http://h2k.invalid'): string {
  const parsed = new URL(url, origin);
  return `${parsed.pathname}${parsed.search}`;
}

/** Record that the game is about to load `url`. */
export function markInGameNavigation(
  url: string,
  store: StorageLike | null = sessionStore(),
  now: number = Date.now(),
): void {
  try {
    store?.setItem(IN_GAME_NAV_KEY, JSON.stringify({ url: pathAndSearchOf(url), at: now }));
  } catch {
    // Storage blocked: the next page treats the URL as typed and lands on /play.
  }
}

/**
 * Consume the pending marker; true when it names `pathAndSearch` and is fresh.
 * Always removes it, so it can vouch for one page load only. `navigationStart`
 * is when this page's navigation began ({@link navigationStartMs}), so the
 * TTL bounds click → navigation, never the download that follows.
 */
export function consumeInGameNavigation(
  pathAndSearch: string,
  store: StorageLike | null = sessionStore(),
  navigationStart: number = navigationStartMs(),
): boolean {
  if (!store) return false;
  try {
    const raw = store.getItem(IN_GAME_NAV_KEY);
    if (raw === null) return false;
    store.removeItem(IN_GAME_NAV_KEY);
    const parsed = JSON.parse(raw) as { url?: unknown; at?: unknown };
    if (parsed.url !== pathAndSearch || typeof parsed.at !== 'number') return false;
    const age = navigationStart - parsed.at;
    return age >= -IN_GAME_NAV_CLOCK_SLACK_MS && age < IN_GAME_NAV_TTL_MS;
  } catch {
    return false;
  }
}

/** True when this history entry was already booted on purpose (a reload, Back). */
export function isTrustedHistoryEntry(historyState: unknown, pathAndSearch: string): boolean {
  if (!historyState || typeof historyState !== 'object') return false;
  return (historyState as Record<string, unknown>)[TRUSTED_BOOT_STATE_KEY] === pathAndSearch;
}

/** Navigate the page as the game (marks the target first). */
export function navigateInGame(url: string, options: { replace?: boolean } = {}): void {
  markInGameNavigation(url);
  if (options.replace) window.location.replace(url);
  else window.location.assign(url);
}
